import { VersaoDTO } from "@/types/pre-producao.type";
import { AprovacaoProgresso } from "@/types/aprovacao-progresso.type";
import { ApprovalScope, TextQualityResult } from "@workflow/types";
import { httpClient } from "./httpClient";

/**
 * Query string das rotas de aprovação. Montada com `URLSearchParams` porque a concatenação
 * anterior gerava `...aprovar&opcaoSelecionadaId=1` (sem `?`) quando não havia arquivo.
 */
function queryAprovacao(
  arquivoAprovacaoId?: number,
  opcaoSelecionadaId?: number,
  dataEvento?: string,
  etapaEsperada?: number,
  scope?: ApprovalScope,
): string {
  const params = new URLSearchParams();
  if (arquivoAprovacaoId) params.set("arquivoAprovacaoId", String(arquivoAprovacaoId));
  if (opcaoSelecionadaId) params.set("opcaoSelecionadaId", String(opcaoSelecionadaId));
  if (dataEvento) params.set("dataEvento", dataEvento);
  if (etapaEsperada) params.set("etapaEsperada", String(etapaEsperada));
  if (scope) params.set("scope", scope);

  const query = params.toString();
  return query ? `?${query}` : "";
}

/** Tarefa de correção criada manualmente na etapa CLIENTE. */
export interface CriarTarefaReprovacao {
  atribuirAId: number;
  /** Opcional: sem prazo, a tarefa é criada sem data de entrega. */
  dataPrazo?: Date;
  observadoresIds?: number[];
}

/** Contexto consultado ao abrir o diálogo de reprovação (fonte da verdade do servidor). */
export interface ContextoReprovacaoVersao {
  temVersaoVigente: boolean;
  podeReprovarPlanilha: boolean;
}

export interface ValidateModerarVersaoResponse {
  hasPendingVersion: boolean;
  canModerate: boolean;
  reason?: "USUARIO_NAO_ESTA_NA_FILA";
  message?: string;
  aprovadoresEtapaAtual?: {
    etapa: number;
    etapaNome: string;
    pessoa: { id: number; nome: string };
  }[];
}

export interface AprovacoesPaginadas {
  data: VersaoDTO[];
  total: number;
  page: number;
  limit: number;
  totalPages?: number;
}

/** Filtros da Central de Aprovações (todas e minhas) resolvidos no servidor. */
export interface ListaAprovacoesParams {
  page?: number;
  limit?: number;
  search?: string;
  tipos?: string[];
  etapas?: string[];
  order?: "asc" | "desc";
}

class AprovacaoService {
  private readonly basePath = "/api/v1/versoes/aprovacoes";

  public async getProgresso(
    versaoIds: number[],
    signal?: AbortSignal,
  ): Promise<AprovacaoProgresso[]> {
    if (versaoIds.length === 0) return [];
    const params = new URLSearchParams({ versaoIds: versaoIds.join(",") });
    return httpClient.get<AprovacaoProgresso[]>(`${this.basePath}/progresso?${params}`, {
      signal,
      loadingConfig: { showGlobalLoading: false },
    });
  }

  /** Central de Aprovações (admin) — paginada e com busca/filtros no servidor. */
  public async listaAprovacoes(
    params: ListaAprovacoesParams = {},
    signal?: AbortSignal,
  ): Promise<AprovacoesPaginadas> {
    return this.listagemPaginada(this.basePath, params, signal);
  }

  /** Fila da própria pessoa — mesma paginação e filtros no servidor. */
  public async listaMinhasAprovacoesPaginadas(
    params: ListaAprovacoesParams = {},
    signal?: AbortSignal,
  ): Promise<AprovacoesPaginadas> {
    return this.listagemPaginada(`${this.basePath}/minhas/listagem`, params, signal);
  }

  private async listagemPaginada(
    path: string,
    { page = 1, limit = 20, search, tipos, etapas, order }: ListaAprovacoesParams,
    signal?: AbortSignal,
  ): Promise<AprovacoesPaginadas> {
    const params = new URLSearchParams({ page: String(page), limit: String(limit) });
    const termo = search?.trim();
    if (termo) params.set("search", termo);
    tipos?.forEach((tipo) => params.append("tipos", tipo));
    etapas?.forEach((etapa) => params.append("etapas", etapa));
    if (order) params.set("order", order.toUpperCase());

    const res = await httpClient.get<AprovacoesPaginadas | VersaoDTO[]>(
      `${path}?${params.toString()}`,
      { signal, loadingConfig: { showGlobalLoading: false } },
    );
    // Tolerante a ambos os formatos: array (legado) ou objeto paginado.
    if (Array.isArray(res)) {
      return { data: res, total: res.length, page, limit };
    }
    return {
      data: res?.data ?? [],
      total: res?.total ?? 0,
      page: res?.page ?? page,
      limit: res?.limit ?? limit,
    };
  }

  public async getVersaoPendenteAprovacao(planilhaId: number): Promise<VersaoDTO | null> {
    return httpClient.get<VersaoDTO | null>(`${this.basePath}/planilha/${planilhaId}/pendente`);
  }

  public async aprovarVersao(
    id: number,
    arquivoAprovacaoId?: number,
    opcaoSelecionadaId?: number,
    /** `YYYY-MM-DD` — obrigatória na etapa CLIENTE enquanto o job não tem data de evento. */
    dataEvento?: string,
    /** Etapa que a tela mostrava; o backend recusa a decisão se a fila já andou. */
    etapaEsperada?: number,
    /** Só para Admin que também é aprovador: só o próprio cargo ou a etapa inteira. */
    scope?: ApprovalScope,
  ): Promise<void> {
    await httpClient.put(
      `${this.basePath}/${id}/aprovar${queryAprovacao(arquivoAprovacaoId, opcaoSelecionadaId, dataEvento, etapaEsperada, scope)}`,
      undefined,
      { toastConfig: { showError: false } },
    );
  }

  public async aprovarParcialmenteVersao(
    id: number,
    arquivoAprovacaoId?: number,
    opcaoSelecionadaId?: number,
    dataEvento?: string,
    etapaEsperada?: number,
  ): Promise<void> {
    await httpClient.put(
      `${this.basePath}/${id}/aprovar-parcialmente${queryAprovacao(arquivoAprovacaoId, opcaoSelecionadaId, dataEvento, etapaEsperada)}`,
      undefined,
      { toastConfig: { showError: false } },
    );
  }

  /** Contexto de reprovação da versão: se a planilha pode ser encerrada junto. */
  public async getContextoReprovacao(versaoId: number): Promise<ContextoReprovacaoVersao> {
    return await httpClient.get<ContextoReprovacaoVersao>(
      `${this.basePath}/${versaoId}/contexto-reprovacao`,
      {
        loadingConfig: { showGlobalLoading: false },
      },
    );
  }

  public async reprovarVersao(
    id: number,
    comentario: string,
    criarTarefa?: CriarTarefaReprovacao,
    /** Quando informado, encerra a planilha com este status junto da reprovação da versão. */
    reprovarPlanilhaBudgetStatus?: string,
    etapaEsperada?: number,
  ): Promise<void> {
    await httpClient.put(
      `${this.basePath}/${id}/reprovar`,
      { comentario, criarTarefa, reprovarPlanilhaBudgetStatus, etapaEsperada },
      { toastConfig: { showError: false } },
    );
  }

  /**
   * Avalia a qualidade do motivo de reprovação/desaprovação. O backend é a
   * fonte da verdade — usado para o feedback ao vivo enquanto o usuário digita.
   * Sem loading global para não piscar a cada tecla.
   */
  public async validarComentarioRecusa(comentario: string): Promise<TextQualityResult> {
    return await httpClient.post<TextQualityResult>(
      `${this.basePath}/validar-comentario-recusa`,
      { comentario },
      { loadingConfig: { showGlobalLoading: false } },
    );
  }

  public async validateModerarVersao(planilhaId: number): Promise<ValidateModerarVersaoResponse> {
    return await httpClient.get<ValidateModerarVersaoResponse>(
      `${this.basePath}/planilha/${planilhaId}/validar-moderacao`,
    );
  }

  public async getNotificacoes(): Promise<number> {
    return await httpClient.get<number>(`${this.basePath}/notificacoes`, {
      loadingConfig: { showGlobalLoading: false },
    });
  }
}

export const aprovacaoService = new AprovacaoService();
